import type { SquirrelEvents } from "../types/sqrl";
import type { FTNavEvents } from "../types/ftnav";
import type { HistoricEventData } from "../types/ftfind";

import { FTNAV_EVENT } from "../types/ftnav/constants";
import { SQRL_EVENT } from "../types/sqrl/constants";
import { SAN_HEADER } from "../types/tracking/constants";
import { ELEMENT_EVENT } from "../types/constants";
import { FTFindHistoryEvent, FTNavEvent, SqrlEvent, TrackingEvent } from "../events";
import { CLASSNAME, KEY, KEY_CODE, SELECTOR } from "../types/element/constants";

export class Searchbar {
  isInitialized = false;

  constructor() {
    if (this.searchIcon && this.header) {
      this.searchIcon.addEventListener(ELEMENT_EVENT.CLICK, this.toggleSearchField.bind(this));
    }

    if (this.searchField && this.header) {
      this.searchField.removeAttribute("disabled");
      this.searchField.addEventListener(ELEMENT_EVENT.FOCUS, this.handleSearchFieldFocus.bind(this));
      this.searchField.addEventListener(ELEMENT_EVENT.KEY_DOWN, this.handleSearchFieldKeydown.bind(this));
    }

    if (this.searchbarAbort) {
      this.searchbarAbort.addEventListener(ELEMENT_EVENT.CLICK, SqrlEvent.close);
    }

    if (this.searchCurtain) {
      this.searchCurtain.addEventListener(ELEMENT_EVENT.CLICK, SqrlEvent.close);
    }

    if (this.header) {
      window.o_global.events<FTNavEvents>().on(FTNAV_EVENT.OPENED, () => this.showSearchField.bind(this));
      window.o_global.events<SquirrelEvents>().on(SQRL_EVENT.SEARCH_OPEN, this.onSearchOpen.bind(this));
      window.o_global.events<SquirrelEvents>().on(SQRL_EVENT.SEARCH_CLOSE, this.onSearchClose.bind(this));
      window.o_global.events<SquirrelEvents>().on(SQRL_EVENT.SEARCH_HIDE, () => this.hideSearchField.bind(this));
    }

    this.isInitialized = true;
  }

  get searchField(): HTMLInputElement {
    return document.querySelector(SELECTOR.SEARCH_FIELD) as HTMLInputElement;
  }

  get searchIcon(): HTMLDivElement {
    return document.querySelector(SELECTOR.SEARCH_ICON) as HTMLDivElement;
  }

  get header(): HTMLElement {
    return document.querySelector(SELECTOR.HEADER_ELEMENT) as HTMLElement;
  }

  get searchbarAbort(): HTMLSpanElement {
    return document.querySelector(SELECTOR.ABORT_BUTTON) as HTMLSpanElement;
  }

  get searchCurtain(): HTMLDivElement {
    return document.querySelector(SELECTOR.CURTAIN) as HTMLDivElement;
  }

  private handleSearchFieldFocus(event: FocusEvent) {
    if (event.isTrusted && !this.header.classList.contains(CLASSNAME.SEARCHBAR_HEADER_ACTIVE)) {
      SqrlEvent.open();
    }
  }

  private handleSearchFieldKeydown(event: KeyboardEvent) {
    const key: string = Boolean(event.which).valueOf() ? event.which + "" : event.code;
    if (key === KEY_CODE.TAB || key === KEY.TAB) {
      const serviceLink = document.querySelector(SELECTOR.SEARCH_ICON) as HTMLElement;
      serviceLink.focus();
      SqrlEvent.close();
    }
  }

  private onSearchOpen(event: HistoricEventData) {
    this.toggleSearchBox(true);
    FTFindHistoryEvent.register(event);
  }

  private onSearchClose(event: HistoricEventData) {
    this.toggleSearchBox(false);
    this.searchField.blur();
    window.o_squirrel.searchSuggest.hideLayer();

    TrackingEvent.trackHeader(SAN_HEADER.CANCEL);
    FTFindHistoryEvent.remove(event);
  }

  private toggleSearchField() {
    const isVisible: boolean = this.header.classList.contains(CLASSNAME.SEARCHBAR_HEADER_VISIBILITY) || false;
    const san_Header: SAN_HEADER = isVisible ? SAN_HEADER.CLOSE : SAN_HEADER.OPEN;

    this.header.classList.toggle(CLASSNAME.SEARCHBAR_HEADER_VISIBILITY);

    if (!isVisible) {
      this.searchField.focus();
      FTNavEvent.close();
    }

    TrackingEvent.trackHeader(san_Header);
  }

  private toggleSearchBox(enable: boolean) {
    if (this.header) {
      this.header.classList.toggle(CLASSNAME.SEARCHBAR_HEADER_ACTIVE, enable);
    }
  }

  hideSearchField() {
    this.header.classList.remove(CLASSNAME.SEARCHBAR_HEADER_VISIBILITY);
  }

  showSearchField() {
    this.header.classList.add(CLASSNAME.SEARCHBAR_HEADER_VISIBILITY);
  }
}

export const searchbar = () => {
  const searchbar = new Searchbar();
  return {
    init: () => searchbar,
    hideSearchField: searchbar.hideSearchField,
    showSearchField: searchbar.showSearchField,
  };
};
